/**
 * Wishlist Module
 * Handles all wishlist functionality including storage, sync, and rendering
 * Uses new RESTful API for authenticated users, localStorage for anonymous
 */

class Wishlist {
  constructor(options = {}) {
    // Consolidated selectors - grouped by context
    this.sel = {
      // Core UI
      icons: '.wishlist-icons',
      button: '.brn-product__fav',
      container: '.collection__grid.wishlist__container',
      empty: '.wishlist__empty',
      loader: '.wishlist_loader',
      login: '.wishlist__login',
      trendy: '.trendy-col',
      overlay: '.wishlist-overlay',

      // Notifications
      notification: '.wishlist__notification',
      notificationPdp: '.wishlist__notification--pdp',
      notificationAdd: '.wishlist__notification__add',
      notificationDelete: '.wishlist__notification__delete',
      notificationMerge: '.wishlist__notification__merge',
      undo: '.wishlist__undo',

      // Mobile drawer
      drawer: '.brn-product__sheet',
      drawerContent: '.brn-product__sheet__content',
      drawerItem: '.brn-product__sheet li',

      // Variants
      variantElem: '.variant-elem',
      addToCart: '.add__to__cart',

      // Size guide
      sizeGuide: '.wishlist_size_guide',
      modelHeight: '.wishlist_height',
      modelSizing: '.wishlist_sizing',
      productSizeGuide: 'size_guide_container',
      productModelHeight: '.brn-product__model',
      productModelSizing: '.brn-product__sizing',
    };

    // Legacy alias for backwards compatibility
    this.elems = {
      ICONS: this.sel.icons,
      HEART: this.sel.button,
      NOTIFICATION_BAR: this.sel.notification,
      NOTIFICATION_BAR_PDP: this.sel.notificationPdp,
      NOTIFICATION_ADDED: this.sel.notificationAdd,
      NOTIFICATION_REMOVED: this.sel.notificationDelete,
      NOTIFICATION_UNDO: this.sel.undo,
      WISHLIST_LOADER: this.sel.loader,
      WISHLIST_CONTAINER: this.sel.container,
      WISHLIST_EMPTY: this.sel.empty,
      WISHLIST_LOGIN: this.sel.login,
      WISHLIST_MOBILE_DRAWER: this.sel.drawer,
      WISHLIST_DESKTOP_ELEM: this.sel.variantElem,
      WISHLIST_MOBILE_DRAWER_CONTENT: this.sel.drawerContent,
      WISHLIST_MOBILE_DRAWER_ELEM: this.sel.drawerItem,
      WISHLIST_OVERLAY: this.sel.overlay,
      ADD_TO_CART_BTN: this.sel.addToCart,
      WISHLIST_TRENDY: this.sel.trendy,
      WISHLIST_SIZE_GUIDE: this.sel.sizeGuide,
      WISHLIST_MODEL_HEIGHT: this.sel.modelHeight,
      WISHLIST_MODEL_SIZING: this.sel.modelSizing,
      PRODUCT_SIZE_GUIDE: this.sel.productSizeGuide,
      PRODUCT_MODEL_HEIGHT: this.sel.productModelHeight,
      PRODUCT_MODEL_SIZING: this.sel.productModelSizing,
    };

    this.customerWishlist = options.customerWishlist;
    this.currentUrl = options.currentUrl;
    this.storefrontAccessToken = options.storefrontAccessToken;
    this.storageKey = options.storageKey;

    // Initialize API client
    this.apiClient = new window.WishlistApiClient({
      baseUrl: options.apiBaseUrl,
    });

    // Local data for anonymous users
    this.data = this.loadFromStorage();

    // Cached product metadata for anonymous users, keyed by product id. Lets the
    // wishlist page render out-of-stock products, which the Storefront API excludes
    // from its results (so fetchProductsFromShopify returns null for them).
    this.localMetaKey = `${this.storageKey}_meta`;
    this.localMeta = this._loadLocalMeta();

    // Cache for API products (logged-in users)
    this.cachedProducts = new Map();

    // Seed cache synchronously from server-rendered metafield data (before any async call)
    if (this.isUserLoggedIn()) {
      this._seedCacheFromServerData();
    }

    // Session storage key for cached product IDs (instant rendering)
    this.sessionCacheKey = 'wishlist_cached_ids';

    // History for undo functionality
    this.history = [];
    this.maxHistorySize = options.maxHistorySize || 10;

    // Optimistic rendering: track pending operations to prevent duplicates
    this.pendingOperations = new Set();

    // API operation queue: prevents race conditions by serializing API calls
    this.apiQueue = Promise.resolve();

    this.isMerging = false;

    this.init();
  }

  /**
   * Check if user is logged in
   */
  isUserLoggedIn() {
    return !!window.customer && window.customer !== '';
  }

  /**
   * Get first available variant for a product
   * Used for API calls that require variant ID
   */
  getFirstAvailableVariant(productId) {
    // First check if we have variant data from button
    const btn = document.querySelector(`${this.elems.HEART}[data-product="${productId}"]`);
    if (btn?.dataset.variant) {
      return btn.dataset.variant;
    }

    // Check if we have variant data in DOM (from wishlist page rendering)
    const variantsJson = document.getElementById(`variants-${productId}`);
    if (variantsJson) {
      try {
        const variants = JSON.parse(variantsJson.textContent);
        const available = variants.find(v => v.availableForSale);
        if (available) {
          return available.id.split('/').pop();
        }
        // Return first variant if none available
        return variants[0]?.id.split('/').pop() || '0';
      } catch {
        return '0';
      }
    }

    return '0';
  }

  // ==========================================
  // Data Normalization - converts any format to unified structure
  // ==========================================

  /**
   * Normalize product data from any source (API or Shopify GraphQL)
   * @param {Object} product - Raw product data
   * @returns {Object} Normalized product
   */
  _normalizeProduct(product) {
    if (!product) return null;

    return {
      id: this._extractId(product),
      title: product.title || product.name || '',
      handle: this._extractHandle(product),
      price: this._extractPrice(product),
      compareAtPrice: this._extractCompareAtPrice(product),
      currency: product.priceRange?.minVariantPrice?.currencyCode || 'EUR',
      available: product.availableForSale ?? product.available ?? (product.global_stock > 0),
      image: this._extractImage(product),
      variants: this._extractVariants(product),
      comingSoon: this._extractComingSoon(product),
    };
  }

  /**
   * Whether the product is in the "reposiciones" collection (coming-soon eligible).
   * Sources, in order: cached-meta flag, Shopify GraphQL collections, API tags.
   */
  _extractComingSoon(product) {
    if (typeof product.comingSoon === 'boolean') return product.comingSoon;

    const handles = product.collections?.nodes;
    if (Array.isArray(handles) && handles.some(c => c?.handle === 'reposiciones' || c?.metafield?.value === '2938')) return true;

    const tags = product.tags;
    if (Array.isArray(tags) && tags.some(t => (t || '').toString().toLowerCase() === 'reposiciones')) return true;

    // Fallback: flag captured from the product's heart button at add-time. Covers
    // logged-in cards (hydrogen API data omits collections) for products added since.
    const id = this._extractId(product);
    if (id && this.localMeta?.[id]?.comingSoon) return true;

    return false;
  }

  _extractId(product) {
    return (product.id_product || product.uniqueID || product.id || '').toString().split('/').pop();
  }

  _extractHandle(product) {
    if (product.handle) return product.handle;

    if (product.link_rewrite) {
      const urlParts = product.link_rewrite.split('/products/');
      const handle = urlParts[urlParts.length - 1]?.split('?')[0];
      if (handle) return handle;
    }

    return (product.title || product.name || '').toLowerCase().replace(/\s+/g, '-');
  }

  _extractPrice(product) {
    return parseFloat(product.price || product.priceRange?.minVariantPrice?.amount || 0);
  }

  _extractCompareAtPrice(product) {
    return parseFloat(product.price_without_reduction || product.compareAtPriceRange?.minVariantPrice?.amount || 0);
  }

  _extractImage(product) {
    if (product.featuredImage) {
      return {
        url: product.featuredImage.url || '',
        alt: product.featuredImage.altText || product.title || product.name || '',
        width: product.featuredImage.width || 2560,
        height: product.featuredImage.height || 3569,
      };
    }

    if (product.media?.length > 0) {
      return {
        url: product.media[0].link || product.media[0].url || '',
        alt: product.title || product.name || '',
        width: 2560,
        height: 3569,
      };
    }

    return { url: '', alt: '', width: 2560, height: 3569 };
  }

  _extractVariants(product) {
    // A size at or below this stock count gets the "last units" clock marker.
    const threshold = window.lowStockThreshold ?? 3;
    const isLowStock = (available, qty) =>
      available && Number.isFinite(qty) && qty > 0 && qty <= threshold;

    // Shopify GraphQL format
    if (product.variants?.nodes) {
      return product.variants.nodes.map(v => {
        const available = v.availableForSale ?? true;
        return {
          id: (v.id || '').toString().split('/').pop(),
          title: v.selectedOptions?.find(opt => window.OptionTypeUtils?.isSize(opt.name))?.value || v.title,
          available,
          lowStock: isLowStock(available, v.quantityAvailable),
        };
      });
    }

    // API format with attrs_group
    if (product.attrs_group) {
      const sizeGroup = product.attrs_group.find(g => window.OptionTypeUtils?.isSize(g.name));
      if (sizeGroup?.attributes) {
        return sizeGroup.attributes.map(attr => {
          const qty = attr.qty ?? attr.quantity ?? 0;
          return {
            id: (attr.id_product_attribute || attr.id || '').toString(),
            title: attr.name,
            available: qty > 0,
            lowStock: isLowStock(qty > 0, qty),
          };
        });
      }
    }

    return [];
  }


  _seedCacheFromServerData() {
    try {
      const raw = this.customerWishlist;
      if (!raw || raw === 'null' || raw === "''" || raw === '') return;

      let parsed;
      try {
        parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      } catch {
        return; // Malformed data — silently skip
      }

      // Schema: { lists: { [id]: { products: string[] } }, metadata: {...} }
      const lists = parsed?.lists;
      if (!lists || typeof lists !== 'object') return;

      let totalSeeded = 0;
      Object.values(lists).forEach(list => {
        const products = list?.products;
        if (!Array.isArray(products)) return;

        products.forEach(id => {
          const idStr = id.toString();
          if (idStr && !this.cachedProducts.has(idStr)) {
            this.cachedProducts.set(idStr, { id_product: idStr, _fromServerSeed: true });
            totalSeeded++;
          }
        });
      });

      if (totalSeeded > 0) {
        console.log('[Wishlist] Seeded', totalSeeded, 'products from server metafield');
      }
    } catch (error) {
      console.warn('[Wishlist] Could not seed from server data:', error);
    }
  }

  /**
   * Extract all product IDs from the server-rendered metafield (window.customerWishlist).
   * Used as a fallback data source when the REST API returns empty.
   * @param {string|null} listId - Specific list ID to extract, or null for all lists
   * @returns {string[]} Array of numeric product ID strings
   * @private
   */
  _getProductIdsFromMetafield(listId = null) {
    try {
      const raw = this.customerWishlist;
      if (!raw || raw === 'null' || raw === "''" || raw === '') return [];

      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const lists = parsed?.lists;
      if (!lists || typeof lists !== 'object') return [];

      const ids = [];
      const targetLists = listId ? (lists[listId] ? [lists[listId]] : []) : Object.values(lists);
      targetLists.forEach(list => {
        (list?.products || []).forEach(id => {
          const idStr = id.toString();
          if (idStr && !ids.includes(idStr)) ids.push(idStr);
        });
      });
      return ids;
    } catch {
      return [];
    }
  }

  /**
   * Migrate product IDs stored in the Shopify metafield (window.customerWishlist)
   * to the REST API via bulk-add. Called when the API is empty but the metafield
   * has data — handles the case where data only lived in the metafield (legacy).
   * @private
   */
  async _migrateMetafieldToApi() {
    const metafieldIds = this._getProductIdsFromMetafield();
    if (metafieldIds.length === 0) return;

    const products = metafieldIds.map(id => ({
      id_product: id,
      id_product_attribute: this.getFirstAvailableVariant(id)
    }));

    try {
      await this.apiClient.bulkAdd(products, '0');
      console.log('[Wishlist] Migrated', metafieldIds.length, 'products from metafield to API');
    } catch (error) {
      console.warn('[Wishlist] Metafield migration to API failed:', error);
    }
  }

  /**
   * Refresh wishlist from API in background (non-blocking)
   * Runs merge, pending actions, and cache refresh without blocking UI
   * @private
   */
  async _refreshFromApiInBackground() {
    try {
      // Merge localStorage products (for users who just logged in)
      await this.mergeLocalStorageToApi();

      // Process any pending single action
      const hadPendingAction = await this.apiClient.processPendingAction();
      if (hadPendingAction) {
        this.showNotification('add');
      }

      // Refresh cache from API and get diff
      const result = await this.refreshCacheFromApi();

      // If API returned empty but metafield has data → migrate metafield → API, then re-fetch
      if (result.apiReturnedEmpty) {
        await this._migrateMetafieldToApi();
        await this.refreshCacheFromApi();  // repopulate cache from now-synced API
        this.updateAllButtons();
        this.updateHeaderWishlistIndicator();
      } else if (result.success && (result.added.length > 0 || result.removed.length > 0)) {
        // Normal diff: update UI only when something actually changed
        console.log('[Wishlist] Cache diff - added:', result.added, 'removed:', result.removed);
        this.updateAllButtons();
        this.updateHeaderWishlistIndicator();
      }
    } catch (error) {
      console.error('[Wishlist] Background refresh failed:', error);
      // Cached UI state remains - graceful degradation
    }
  }

  async init() {
    const icons = document.querySelector(this.elems.ICONS);
    if (icons) {
      this.heartIcon = icons.querySelector('.heart');
      this.cartIcon = icons.querySelector('.wishlist-cart');
    }

    this.attachEventListeners();
    this.initNotifications();

    if (this.isUserLoggedIn()) {
      const hadCache = this.loadCacheFromSession();
      if (hadCache || this.cachedProducts.size > 0) {
        this.updateAllButtons();
        this.updateHeaderWishlistIndicator();
      }

      // 2. Prepare local products for merge (if user just logged in)
      this.prepareLocalProductsForMerge();

      // 3. BACKGROUND / FOREGROUND: Run API operations (merge, pending actions, cache refresh)
      if (this.currentUrl.includes('/pages/wishlist')) {
        // On the wishlist page we MUST await the full refresh so renderWishlist
        // receives fresh data and doesn't render an empty list due to the race condition.
        await this._refreshFromApiInBackground();
      } else {
        // On every other page keep it non-blocking for best perceived performance
        this._refreshFromApiInBackground();
      }
    } else {
      // Anonymous user: load from localStorage and update UI
      this.updateAllButtons();
      this.updateHeaderWishlistIndicator();
    }

    // Render wishlist page if we're on it
    if (this.currentUrl.includes('/pages/wishlist')) {
      await this.renderWishlist(this.elems.WISHLIST_CONTAINER);

      // Show merge notification if a previous merge (on any page) added products.
      // The flag survives page navigations within the same browser tab (sessionStorage).
      if (sessionStorage.getItem('wishlist_show_merge_notification')) {
        sessionStorage.removeItem('wishlist_show_merge_notification');
        setTimeout(() => this.showNotification('merge'), 500);
      }
    }
  }

  /**
   * Merge localStorage/sessionStorage wishlist to API using bulk endpoint
   */
  async mergeLocalStorageToApi() {
    const pendingMergeKey = 'wishlist_pending_merge';

    // Leer de sessionStorage (preparado por prepareLocalProductsForMerge)
    const pendingMerge = sessionStorage.getItem(pendingMergeKey);
    if (!pendingMerge) return;

    let localProducts;
    try {
      localProducts = JSON.parse(pendingMerge);
    } catch {
      sessionStorage.removeItem(pendingMergeKey);
      return;
    }

    if (!localProducts || localProducts.length === 0) {
      sessionStorage.removeItem(pendingMergeKey);
      return;
    }

    // Preparar productos para bulk endpoint
    const products = localProducts.map(productId => ({
      id_product: productId.toString(),
      id_product_attribute: this.getFirstAvailableVariant(productId)
    }));

    try {
      this.isMerging = true;
      const result = await this.apiClient.bulkAdd(products, '0');

      // Limpiar sessionStorage después de éxito
      sessionStorage.removeItem(pendingMergeKey);

      // Log resultado
      if (result?.meta) {
        console.log(`[Wishlist] Merge complete: ${result.meta.added} added, ${result.meta.alreadyExisted} already existed`);
      }

      // Deferred merge notification: store a flag so the wishlist page shows it
      // whenever the user gets there (same page load or a future navigation).
      if (result?.meta?.added > 0) {
        sessionStorage.setItem('wishlist_show_merge_notification', 'true');
      }
      this.isMerging = false;
    } catch (error) {
      this.isMerging = false;
      // Si da 401, sessionStorage mantiene los productos para reintentar después del login
      console.error('[Wishlist] Bulk merge failed:', error);

      // Solo propagar si es error de auth (para que handleAuthError actúe)
      if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        throw error;
      }
    }
  }

  /**
   * Clear products from localStorage (keep structure)
   */
  clearLocalProducts() {
    const data = this.loadFromStorage();
    if (data?.lists?.['0']) {
      data.lists['0'].products = [];
      data.lists['0'].updatedAt = new Date().toISOString();
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    }
    this.data = data;
  }

  /**
   * Move localStorage products to sessionStorage before API merge
   * This prevents duplicate merge if there's a 401 redirect
   */
  prepareLocalProductsForMerge() {
    const pendingMergeKey = 'wishlist_pending_merge';

    // Si ya hay pending merge en sessionStorage, no sobrescribir
    if (sessionStorage.getItem(pendingMergeKey)) return;

    const localProducts = this.data?.lists?.['0']?.products || [];
    if (localProducts.length === 0) return;

    // Mover a sessionStorage
    sessionStorage.setItem(pendingMergeKey, JSON.stringify(localProducts));

    // Limpiar localStorage inmediatamente
    this.clearLocalProducts();

    console.log('[Wishlist] Moved', localProducts.length, 'products to sessionStorage for merge');
  }

  /**
   * Load cached product IDs from sessionStorage for instant UI rendering
   * Validates that the cached user matches the current user
   * @returns {boolean} true if cache was loaded, false if empty or invalid
   */
  loadCacheFromSession() {
    try {
      const cached = sessionStorage.getItem(this.sessionCacheKey);
      if (!cached) return false;

      const { userId, productIds } = JSON.parse(cached);

      // Validate that the cached user matches the current user
      const currentUserId = window.customer?.toString() || '';
      if (userId !== currentUserId) {
        // Different user: invalidate cache
        sessionStorage.removeItem(this.sessionCacheKey);
        console.log('[Wishlist] Session cache invalidated: user mismatch');
        return false;
      }

      if (!Array.isArray(productIds) || productIds.length === 0) return false;

      // Populate cachedProducts with placeholder objects
      productIds.forEach(id => {
        this.cachedProducts.set(id.toString(), {
          id_product: id.toString(),
          _fromCache: true
        });
      });

      console.log('[Wishlist] Loaded', productIds.length, 'products from session cache');
      return true;
    } catch (error) {
      console.error('[Wishlist] Error loading session cache:', error);
      return false;
    }
  }

  /**
   * Save current product IDs to sessionStorage for future page loads
   * Includes user ID for validation on load
   */
  saveCacheToSession() {
    try {
      const currentUserId = window.customer?.toString() || '';
      const productIds = Array.from(this.cachedProducts.keys());

      sessionStorage.setItem(this.sessionCacheKey, JSON.stringify({
        userId: currentUserId,
        productIds: productIds
      }));

      console.log('[Wishlist] Saved', productIds.length, 'products to session cache');
    } catch (error) {
      console.error('[Wishlist] Error saving session cache:', error);
    }
  }

  /**
   * Refresh cached products from API
   * Used after login redirect to sync local cache with server state
   * @returns {Object} { success, added, removed } for diff detection
   */
  async refreshCacheFromApi(listId = '0') {
    try {
      // Capture previous state for diff detection
      const previousIds = new Set(this.cachedProducts.keys());

      const response = await this.apiClient.getWishlist(listId);
      const products = response?.data?.products || [];

      // Guard: if the API returned empty but the metafield has data, the API is out of
      // sync (migration scenario). Keep the seeded cache intact and signal the caller
      // so it can trigger migration instead of nuking valid local state.
      if (products.length === 0 && this._getProductIdsFromMetafield(listId).length > 0) {
        console.warn('[Wishlist] API returned empty but metafield has data — skipping cache clear, triggering migration');
        return { success: true, added: [], removed: [], apiReturnedEmpty: true };
      }

      this.cachedProducts.clear();
      products.forEach(p => {
        const id = (p.id_product || p.uniqueID || '').toString();
        if (id) this.cachedProducts.set(id, p);
      });

      // Save to sessionStorage for future page loads
      this.saveCacheToSession();

      // Calculate diff for UI reconciliation
      const currentIds = new Set(this.cachedProducts.keys());
      const added = [...currentIds].filter(id => !previousIds.has(id));
      const removed = [...previousIds].filter(id => !currentIds.has(id));

      return { success: true, added, removed, apiReturnedEmpty: false };
    } catch (error) {
      console.error('[Wishlist] Failed to refresh cache:', error);
      return { success: false, added: [], removed: [] };
    }
  }

  attachEventListeners() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest(this.elems.HEART);
      if (btn) {
        e.preventDefault();
        const productId = btn.dataset.product;

        // OPTIMISTIC: Update button state immediately before API call
        const willAdd = !this.hasProduct(productId);
        btn.classList.toggle('active', willAdd);
        btn.setAttribute('aria-pressed', willAdd.toString());
        this.triggerWiggle(btn);

        // Toggle in background (no await needed for instant feedback)
        this.toggleProduct(productId, '0');
      }

    });

    // Card quick-add (add to cart, size drawer, size guide) is owned by
    // assets/quick-add-cart.js. We only mirror the legacy "remove from wishlist
    // when added to cart" behaviour, via the shared cartUpdate event.
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source !== 'quick-add') return;
      const productId = event.productId;
      if (productId && this.hasProduct(productId)) {
        this.removeProduct(productId, '0', false, true);
        this.updateAllButtons();
      }
    });
  }

  triggerWiggle(btn) {
    btn.classList.remove('wiggle');
     
    void btn.offsetWidth;
    btn.classList.add('wiggle');
    btn.addEventListener('animationend', () => btn.classList.remove('wiggle'), { once: true });
  }

  getDefaultStructure() {
    const now = new Date().toISOString();
    return {
      lists: {
        '0': { id: '0', name: 'My Wishlist', products: [], createdAt: now, updatedAt: now }
      }
    };
  }

  updateAllButtons() {
    document.querySelectorAll(this.elems.HEART).forEach(btn => {
      const productId = btn.dataset.product;
      const listId = btn.dataset.list || '0';
      if (productId) {
        this.updateButtonState(btn, productId, listId);
      }
    });
  }

  updateButtonState(button, productId, listId = '0') {
    const isInWishlist = this.hasProduct(productId, listId);
    if (isInWishlist) {
      button.classList.add('active');
    } else {
      button.classList.remove('active');
    }

    button.setAttribute('aria-pressed', isInWishlist);
  }

  initNotifications() {
    // Notifications now render through the unified toaster (window.toast); these
    // listeners keep doing the wishlist-page UI work (card removal, empty state).
    document.addEventListener('wishlist:productAdded', async (e) => {
      if (!this.isMerging) {
        this.showNotification('add');
      }
      const emptyBlock = document.querySelector(this.elems.WISHLIST_EMPTY);
      if (emptyBlock) {
        emptyBlock.style.display = 'none';
      }

      const wishlistLoginBanner = document.querySelector(this.elems.WISHLIST_LOGIN);
      if (wishlistLoginBanner) {
        wishlistLoginBanner.classList.add('active');
      }

      // OPTIMISTIC: Clone card from trendy section instead of re-rendering
      if (this.currentUrl.includes('pages/wishlist')) {
        const productId = e.detail?.productId;
        if (productId) {
          this._addCardFromTrendyOptimistic(productId);
        }
      }

      this.updateAllButtons();
    });

    // Handle failed operations - rollback UI
    document.addEventListener('wishlist:productActionFailed', (e) => {
      const { action, productId } = e.detail;

      // Show error notification
      console.error(`[Wishlist] ${action} failed for product ${productId}`);

      // Remove optimistically added card if exists
      const optimisticCard = document.querySelector(`.wishlist-optimistic [data-product="${productId}"]`);
      if (optimisticCard) {
        const gridItem = optimisticCard.closest('.collection__grid__item');
        if (gridItem) gridItem.remove();
      }

      this.updateAllButtons();
      this._updateTrendyVisibility();
    });

    document.addEventListener('wishlist:productRemoved', (e) => {
      if (!e.detail.hideNotification) {
        this.showNotification('delete');
      }
      const productDeleted = e.detail.productId;

      const elems = document.querySelectorAll(this.elems.WISHLIST_CONTAINER + ` .brn-product__fav[data-product="${productDeleted}"]`);
      elems.forEach(btn => {
        const gridItem = btn.closest('.collection__grid__item');
        if (gridItem) {
          gridItem.remove();
        }
      });

      this.updateAllButtons();

      const totalProducts = this.getTotalProductCount();

      if (totalProducts === 0) {
        const wishlistLoginBanner = document.querySelector(this.elems.WISHLIST_LOGIN);
        if (wishlistLoginBanner) {
          wishlistLoginBanner.classList.remove('active');
        }
        const emptyBlock = document.querySelector(this.elems.WISHLIST_EMPTY);
        if (emptyBlock) {
          emptyBlock.style.display = 'block';
        }
      }
    });

    document.addEventListener('wishlist:actionUndone', async (e) => {
      if (e.detail.action === 'add') {
        this.showNotification('delete', false);

        const totalProducts = this.getTotalProductCount();

        if (totalProducts === 0) {
          const emptyBlock = document.querySelector(this.elems.WISHLIST_EMPTY);
          if (emptyBlock) {
            emptyBlock.style.display = 'block';
          }
          const wishlistLoginBanner = document.querySelector(this.elems.WISHLIST_LOGIN);
          if (wishlistLoginBanner) {
            wishlistLoginBanner.classList.remove('active');
          }
        }
      } else if (e.detail.action === 'remove') {
        this.showNotification('add', false);

        if (this.currentUrl.includes('pages/wishlist')) {
          await this.renderWishlist(this.elems.WISHLIST_CONTAINER);
        }
      }

      this.updateAllButtons();
    });

    // Handle refresh request from cart (when product is moved to wishlist from cart drawer)
    document.addEventListener('wishlist:pageRefreshRequested', async (e) => {
      if (!this.currentUrl.includes('pages/wishlist')) return;

      const productId = e.detail?.productId;
      if (!productId) return;

      // Hide empty state if visible
      const emptyBlock = document.querySelector(this.elems.WISHLIST_EMPTY);
      if (emptyBlock) {
        emptyBlock.style.display = 'none';
      }

      // Wait for pending API operations to complete before fetching updated list
      if (this.apiQueue) {
        await this.apiQueue;
      }

      // Re-render the wishlist to show the new product
      await this.renderWishlist(this.elems.WISHLIST_CONTAINER);
      this.updateAllButtons();
    });
  }

  /**
   * Get total product count (from local data or cached)
   */
  getTotalProductCount() {
    if (this.isUserLoggedIn()) {
      return this.cachedProducts.size;
    }
    return Object.values(this.data.lists)
      .reduce((sum, list) => sum + list.products.length, 0);
  }

  showNotification(type, showUndo = true) {
    const onWishlistPage = this.currentUrl.includes('/pages/wishlist');

    // 'merge' notification: only meaningful on the wishlist page itself
    if (type === 'merge' && !onWishlistPage) return;

    // all other notifications: suppress on the wishlist page (handled differently there)
    if (type !== 'merge' && onWishlistPage) return;

    const s = window.wishlistStrings || {};

    if (type === 'add') {
      window.toast?.show(s.added || 'Added to wishlist', {
        icon: 'heart',
        action: onWishlistPage ? null : { label: s.see, href: s.seeUrl },
      });
    } else if (type === 'delete') {
      window.toast?.show(s.deleted || 'Removed from wishlist', {
        icon: 'broken-heart',
        action: showUndo ? { label: s.undo, onClick: () => this.undoLastAction() } : null,
      });
    } else if (type === 'merge') {
      window.toast?.show(s.merged || 'Wishlist merged', { icon: 'info' });
    }
  }

  hideNotification() {
    window.toast?.hide();
  }

  loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading wishlist from storage:', error);
    }
    return this.getDefaultStructure();
  }

  saveToStorage() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.data));
    } catch (error) {
      console.error('Error saving wishlist to storage:', error);
    }
  }

  _loadLocalMeta() {
    try {
      const stored = localStorage.getItem(this.localMetaKey);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error loading wishlist meta from storage:', error);
      return {};
    }
  }

  _saveLocalMeta() {
    try {
      localStorage.setItem(this.localMetaKey, JSON.stringify(this.localMeta));
    } catch (error) {
      console.error('Error saving wishlist meta to storage:', error);
    }
  }

  /**
   * Snapshot a product's display data (title, price, image, handle, variant) from its
   * heart button, in the same shape as a Shopify Storefront product node. Stored at
   * add-time so renderWishlist can draw a card even when the Storefront API later
   * excludes the product (out-of-stock products are dropped from the storefront context).
   */
  _cacheProductMeta(productId) {
    const btn = document.querySelector(`${this.elems.HEART}[data-product="${productId}"]`);
    if (!btn) return;

    const idStr = productId.toString();
    const rawPrice = parseFloat(btn.dataset.price || '0');
    // data-price is in the currency's minor units (cents); the normalizer expects a decimal amount.
    const amount = rawPrice ? (rawPrice / 100).toString() : '0';
    const imageUrl = btn.dataset.image || '';
    const variantTitle = btn.dataset.variantTitle || '';

    this.localMeta[idStr] = {
      id: `gid://shopify/Product/${idStr}`,
      title: btn.dataset.title || '',
      handle: btn.dataset.handle || '',
      availableForSale: false,
      priceRange: { minVariantPrice: { amount, currencyCode: window.currency || 'EUR' } },
      featuredImage: imageUrl
        ? { url: imageUrl, altText: btn.dataset.title || '', width: 2560, height: 3569 }
        : null,
      variants: {
        nodes: [{
          id: `gid://shopify/ProductVariant/${btn.dataset.variant || '0'}`,
          title: variantTitle,
          availableForSale: false,
          selectedOptions: [{ name: 'size', value: variantTitle }],
        }],
      },
      comingSoon: btn.dataset.comingSoon === 'true',
      _fromLocalMeta: true,
    };
    this._saveLocalMeta();
  }

  updateHeaderWishlistIndicator() {
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (!cartIcon) return;

    if (this.currentUrl.includes('pages/wishlist')) {
      cartIcon.classList.add('has-wishlist-items');
    } else {
      cartIcon.classList.remove('has-wishlist-items');
    }
  }

  async toggleProduct(productId, listId = '0') {
    // Prevent duplicate operations on same product
    if (this.pendingOperations.has(productId)) return false;
    this.pendingOperations.add(productId);

    try {
      if (this.hasProduct(productId, listId)) {
        return await this.removeProduct(productId, listId);
      } else {
        return await this.addProduct(productId, listId);
      }
    } finally {
      this.pendingOperations.delete(productId);
    }
  }

  /**
   * Check if product is in wishlist
   * For logged-in users, check cached data
   * For anonymous users, check localStorage
   */
  hasProduct(productId, listId = '0') {
    if (this.isUserLoggedIn()) {
      return this.cachedProducts.has(productId.toString());
    }
    const list = this.data.lists[listId];
    return list ? list.products.includes(productId.toString()) : false;
  }

  // ==========================================
  // Storage Operations - unified add/remove with delegated methods
  // ==========================================

  /**
   * Add product to wishlist
   */
  async addProduct(productId, listId = '0') {
    const productIdStr = productId.toString();
    const variantId = this.getFirstAvailableVariant(productIdStr);

    const btn = document.querySelector(`${this.elems.HEART}[data-product="${productIdStr}"]`);
    const productTitle = btn?.dataset.title || '';
    const productVariant = btn?.dataset.variantTitle
    const productPrice = btn?.dataset.price

    // OPTIMISTIC: Update local state immediately
    if (this.isUserLoggedIn()) {
      // Add placeholder to cache - will be updated with full data from API
      this.cachedProducts.set(productIdStr, { id_product: productIdStr, _pending: true });
    } else {
      this._addToLocal(productIdStr, listId);
    }
    // Snapshot display metadata (incl. the reposiciones/coming-soon flag) for both
    // auth states, so the wishlist can render it even when the live source omits it.
    this._cacheProductMeta(productIdStr);

    // OPTIMISTIC: Dispatch event immediately for instant UI update
    this.addToHistory({ action: 'add', productId: productIdStr, variantId, listId });
    this.dispatchEvent('productAdded', { productId: productIdStr, listId, productTitle, productVariant, productPrice, variantId });
    this.updateHeaderWishlistIndicator();

    // API call in background (only for logged-in users)
    if (this.isUserLoggedIn()) {
      this._addToApiAsync(productIdStr, variantId, listId);
    }

    return true;
  }

  /**
   * Enqueue an API operation to prevent race conditions
   * Operations execute sequentially while UI remains optimistic
   */
  _enqueueApiOperation(operation) {
    this.apiQueue = this.apiQueue
      .then(() => operation())
      .catch(error => {
        console.error('[Wishlist] Queued operation failed:', error);
        throw error;
      });
    return this.apiQueue;
  }

  /**
   * Async API add with rollback on failure (queued to prevent race conditions)
   * Expects reduced response: { success: true, data: { productIds: [...] }}
   */
  _addToApiAsync(productId, variantId, listId) {
    return this._enqueueApiOperation(async () => {
      try {
        const result = await this.apiClient.addProduct(productId, variantId, listId);
        // Reduced response: just confirm success and keep placeholder in cache
        if (result?.success || result?.data) {
          this.cachedProducts.set(productId, { id_product: productId });
        }
        // Update session cache after successful add
        this.saveCacheToSession();
      } catch (error) {
        console.error('[Wishlist] API add failed, rolling back:', error);
        this._rollbackAdd(productId, listId);
      }
    });
  }

  _rollbackAdd(productId, listId) {
    this.cachedProducts.delete(productId);
    this.dispatchEvent('productRemoved', { productId, listId, hideNotification: true });
    this.dispatchEvent('productActionFailed', { action: 'add', productId });
  }

  /**
   * Remove product from wishlist
   */
  async removeProduct(productId, listId = '0', skipHistory = false, hideNotification = false) {
    const productIdStr = productId.toString();
    const variantId = this.getFirstAvailableVariant(productIdStr);

    const btn = document.querySelector(`${this.elems.HEART}[data-product="${productIdStr}"]`);
    const productTitle = btn?.dataset.title || '';
    const productVariant = btn?.dataset.variantTitle

    // Save state for potential rollback
    const previousState = this.isUserLoggedIn()
      ? this.cachedProducts.get(productIdStr)
      : null;

    // OPTIMISTIC: Update local state immediately
    if (this.isUserLoggedIn()) {
      this.cachedProducts.delete(productIdStr);
    } else {
      this._removeFromLocal(productIdStr, listId);
      delete this.localMeta[productIdStr];
      this._saveLocalMeta();
    }

    // OPTIMISTIC: Dispatch event immediately for instant UI update
    if (!skipHistory) {
      this.addToHistory({ action: 'remove', productId: productIdStr, variantId, listId });
    }
    this.dispatchEvent('productRemoved', { productId: productIdStr, listId, hideNotification, productTitle, productVariant, variantId });
    this.updateHeaderWishlistIndicator();

    // API call in background (only for logged-in users)
    if (this.isUserLoggedIn()) {
      this._removeFromApiAsync(productIdStr, variantId, listId, previousState);
    }

    return true;
  }

  /**
   * Async API remove with rollback on failure (queued to prevent race conditions)
   */
  _removeFromApiAsync(productId, variantId, listId, previousState) {
    return this._enqueueApiOperation(async () => {
      try {
        await this.apiClient.removeProduct(productId, variantId, listId);
        // Update session cache after successful remove
        this.saveCacheToSession();
      } catch (error) {
        console.error('[Wishlist] API remove failed, rolling back:', error);
        this._rollbackRemove(productId, listId, previousState);
      }
    });
  }

  _rollbackRemove(productId, listId, previousState) {
    if (previousState) {
      this.cachedProducts.set(productId, previousState);
    }
    this.dispatchEvent('productAdded', { productId, listId });
    this.dispatchEvent('productActionFailed', { action: 'remove', productId });
  }

  _addToLocal(productId, listId) {
    const list = this.data.lists[listId];
    if (!list) return false;

    if (list.products.includes(productId)) return false;

    list.products.push(productId);
    list.updatedAt = new Date().toISOString();
    this.saveToStorage();
    return true;
  }

  _removeFromLocal(productId, listId) {
    const list = this.data.lists[listId];
    if (!list) return false;

    const index = list.products.indexOf(productId);
    if (index === -1) return false;

    list.products.splice(index, 1);
    list.updatedAt = new Date().toISOString();
    this.saveToStorage();
    return true;
  }

  addToHistory(action) {
    this.history.push({ ...action, timestamp: Date.now() });
    if (this.history.length > this.maxHistorySize) {
      this.history.shift();
    }
  }

  async undoLastAction() {
    if (this.history.length === 0) {
      console.warn('No actions to undo');
      return false;
    }

    const lastAction = this.history.pop();

    if (lastAction.action === 'add') {
      await this.removeProduct(lastAction.productId, lastAction.listId, true);
      this.dispatchEvent('actionUndone', {
        action: 'add',
        productId: lastAction.productId,
        listId: lastAction.listId
      });
    } else if (lastAction.action === 'remove') {
      await this.addProduct(lastAction.productId, lastAction.listId);
      // Remove the undo from history (addProduct adds to history)
      this.history.pop();
      this.dispatchEvent('actionUndone', {
        action: 'remove',
        productId: lastAction.productId,
        listId: lastAction.listId
      });
    }

    this.updateAllButtons();
    return true;
  }

  getHistory() {
    return [...this.history];
  }

  clearHistory() {
    this.history = [];
  }

  dispatchEvent(eventName, detail) {
    const event = new CustomEvent(`wishlist:${eventName}`, {
      detail,
      bubbles: true
    });
    document.dispatchEvent(event);
  }

  clear() {
    this.data = this.getDefaultStructure();
    this.cachedProducts.clear();
    this.saveToStorage();
    this.updateAllButtons();
  }

  /**
   * Fetch products from Shopify Storefront API (fallback for anonymous users)
   */
  async fetchProductsFromShopify(productIds) {
    if (!productIds || productIds.length === 0) {
      return [];
    }

    const gids = productIds.map(id => {
      if (id.startsWith('gid://shopify/Product/')) {
        return id;
      }
      return `gid://shopify/Product/${id}`;
    });

    const query = `
      {
        nodes(ids: ${JSON.stringify(gids)}) {
          ... on Product {
            id
            title
            handle
            description
            requiresSellingPlan
            availableForSale
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            compareAtPriceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            featuredImage {
              url
              altText
              width
              height
            }
            variants(first: 10) {
                nodes {
                  id
                  title
                  availableForSale
                  quantityAvailable
                  selectedOptions {
                    name
                    value
                  }
                  price {
                    amount
                    currencyCode
                  }
              }
            }
            tags
            vendor
            collections(first: 50) {
              nodes {
                handle
                metafield(namespace: "custom", key: "id_prestashop") {
                  value
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch(`${window.shopUrl}/api/2025-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken || ''
        },
        body: JSON.stringify({ query })
      });

      if (!response.ok) {
        throw new Error(`Shopify API error: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.errors) {
        console.error('GraphQL errors:', data.errors);
        return null;
      }
      return data.data.nodes;
    } catch (error) {
      console.error('Error fetching products from Shopify:', error);
      return null;
    }
  }

  /**
   * "Avísame" opener targeting the global coming-soon modal (#QuickAddComingSoonModal).
   * Mirrors snippets/quick-add-notify-link.liquid so quick-add-cart.js + cart-drawer.js
   * drive it the same way as server-rendered cards.
   */
  _notifyLinkHTML(variantId, productId, image, label, modifier = '') {
    const mail = this._mailIconHTML();
    return `<modal-opener class="brn-coming-soon ${modifier}" data-modal="#QuickAddComingSoonModal">` +
      `<button type="button" class="brn-coming-soon__link" data-variant-id="${variantId}" data-product-id="${productId}" data-product-image="${image || ''}">` +
      `${mail}<span class="brn-coming-soon__text">${label}</span></button></modal-opener>`;
  }

  /**
   * Render a product card using normalized data
   */
  renderProductCard(rawProduct, options = {}) {
    const { loading = 'lazy' } = options;
    const product = this._normalizeProduct(rawProduct);
    if (!product) return '';

    const { id, title, handle, price, compareAtPrice, currency, available, image, variants } = product;
    const singleProduct = variants.length <= 1;
    const firstVariantId = variants[0]?.id || '0';

    // Multi-variant product whose every size is out of stock: the desktop size
    // list collapses into a single "Avísame" CTA inside .variants-container.
    const allVariantsOut = variants.length > 1 && variants.every(v => !v.available);

    // Coming soon ("Avísame") is only eligible for products in the "reposiciones"
    // collection. When all variants are out, such cards show the CTA instead of the
    // "Producto agotado" ribbon; non-reposiciones sold-out cards keep the ribbon.
    const comingSoon = product.comingSoon === true;
    const showNotify = !available && comingSoon;
    const notifyCardLabel = window.variantStrings?.notifyCard || 'Coming soon';
    const notifyLinkLabel = window.variantStrings?.notifyLink || '¡Avísame!';
    const comingSoonCTA = this._notifyLinkHTML(firstVariantId, id, image.url, notifyCardLabel, 'brn-coming-soon--card');
    const comingSoonDesktop = `<div class="variants-container variants-container--coming-soon">${this._notifyLinkHTML(firstVariantId, id, image.url, notifyCardLabel, 'brn-coming-soon--desktop')}</div>`;

    // Price formatting
    const formattedPrice = new Intl.NumberFormat('es-ES', { style: 'currency', currency }).format(price);
    const formattedComparePrice = compareAtPrice > 0
      ? new Intl.NumberFormat('es-ES', { style: 'currency', currency }).format(compareAtPrice)
      : null;
    const discount = compareAtPrice > price ? ((compareAtPrice - price) / compareAtPrice) * 100 : null;

    // Wishlist state
    const isInWishlist = this.hasProduct(id);
    const activeClass = isInWishlist ? 'active' : '';
    const ariaPressed = isInWishlist ? 'true' : 'false';

    // Image srcset
    const urlSeparator = image.url.includes('?') ? '&' : '?';
    const srcset = image.url ? `
      ${image.url}${urlSeparator}width=375 375w,
      ${image.url}${urlSeparator}width=750 750w,
      ${image.url}${urlSeparator}width=1000 1000w,
      ${image.url}${urlSeparator}width=1440 1440w
    `.trim() : '';

    // Variants HTML
    const variantsHTML = this._buildVariantsHTML(id, handle, variants, singleProduct, firstVariantId, image.url, comingSoon);

    return `
      <div class="collection__grid__item scroll-trigger">
        <color-swatch>
          <a class="brn-card" href="${window.Shopify.routes.root}products/${handle}" aria-label="Open product page" tabindex="0">
            <div class="brn-card__image" data-available="${available}">
              ${available ? `
              <button type="button" class="add__to__cart add__to__cart--tablet"
                data-product-id="${id}" data-first-variant="${firstVariantId}" data-title="${title}" data-variant-title="${variants[0]?.title}" data-price="${formattedPrice}"
                data-single="${singleProduct}" data-product-handle="${handle}" data-coming-soon="${comingSoon}" data-product-image="${image.url}" data-prefetched="false">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="24" height="24" fill="white" fill-opacity="0.6"/>
                  <path d="M12.0013 6.4746V18.1413M6.168 12.3079H17.8346" stroke="#2A2A2A" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              ` : (showNotify ? '' : '')}
              <div class="add__to__cart--desktop ${showNotify ? 'add__to__cart--desktop--coming-soon' : ''}">
                ${available
    ? `${variants.length > 1 && !allVariantsOut ? `<p>${window.variantStrings?.addToCart || 'Add to cart'}</p>` : ''}<div class="variants-container${allVariantsOut ? ' variants-container--coming-soon' : ''}">${variantsHTML}</div>`
    : (showNotify ? comingSoonDesktop : '')}
              </div>
              ${showNotify ? `<div class="coming-soon">Coming soon</div>` : `<div class="coming-soon">${window.variantStrings.prodSoldOut}</div>`}
              <picture class="brn-card__picture" data-uri="${handle}">
                <div class="card__loader hidden"></div>
                ${image.url ? `
                  <img class="card__img" srcset="${srcset}" sizes="(min-width: 1000px) 620px, 100vw"
                    src="${image.url}${urlSeparator}width=1000" alt="${image.alt}"
                    loading="${loading}" width="${image.width}" height="${image.height}">
                ` : ''}
              </picture>
            </div>
            <div class="brn-card__footer">
              <div class="brn-card__content">
                <h3 class="brn-card__title">${title}</h3>
                <div class="card__price">
                  <div class="price">
                    <div class="price__container">
                      <div class="price__sale visible">
                        <div class="price__sale__details-wrapper">
                          ${formattedComparePrice && discount ? `
                            <span class="price-item price-item--last">${formattedPrice}</span>
                            <span class="price-item price-item--percent">${discount.toFixed(0)}%</span>
                            <s class="price-item price-item--sale">${formattedComparePrice}</s>
                          ` : `<span class="price-item">${formattedPrice}</span>`}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <script type="application/json" id="variants-${id}">
                  ${JSON.stringify(this._variantsForJson(variants))}
                </script>
                ${showNotify || allVariantsOut ? `
                <div class="add__to__cart--mobile add__to__cart--mobile--coming-soon">
                  ${this._notifyLinkHTML(firstVariantId, id, image.url, notifyLinkLabel, 'brn-coming-soon--mobile')}
                </div>
                ` : `
                <div class="add__to__cart add__to__cart--mobile"
                  data-product-id="${id}" data-first-variant="${firstVariantId}"
                  data-single="${singleProduct}" data-product-handle="${handle}" data-coming-soon="${comingSoon}" data-product-image="${image.url}" data-prefetched="false">
                  ${this.cartIcon?.outerHTML || ''}
                  <p>${window.variantStrings?.addToCart || 'Add to cart'}</p>
                </div>
                `}
              </div>
              <div class="brn-card__actions">
                <button class="btn btn-icon brn-product__fav ${activeClass}" type="button"
                  data-product="${id}" data-variant="${firstVariantId}" aria-pressed="${ariaPressed}">
                  <span class="sr-only">Like</span>
                  ${this.heartIcon?.outerHTML || ''}
                </button>
              </div>
            </div>
          </a>
        </color-swatch>
      </div>
    `;
  }

  /**
   * Build variants HTML for desktop/mobile display.
   * comingSoon → out-of-stock sizes get the mail icon that opens the global
   * coming-soon modal (quick-add-cart.js routes the click off the pill's class).
   */
  _buildVariantsHTML(productId, handle, variants, singleProduct, firstVariantId, imageUrl = '', comingSoon = false) {
    if (variants.length <= 1) {
      return `<div class="add__to__cart add__to__cart--desktop-single"
        data-product-id="${productId}" data-first-variant="${firstVariantId}"
        data-product-handle="${handle}" data-single="${singleProduct}" data-prefetched="false">
        ${this.cartIcon?.outerHTML || ''}
      </div>`;
    }

    // Every size out of stock: collapse the size list into a single "Avísame"
    // link (mail icon + label) that opens the coming-soon modal, instead of a
    // row of unavailable variant-elems.
    if (variants.every(v => !v.available)) {
      const notifyLabel = window.variantStrings?.notifyLink || '¡Avísame!';
      return this._notifyLinkHTML(firstVariantId, productId, imageUrl, notifyLabel, 'brn-coming-soon--desktop');
    }

    const clock = this._clockIconHTML();
    const mail = this._mailIconHTML();
    return variants.map(v => {
      const notify = comingSoon && !v.available;
      const modifier =
        (v.lowStock ? ' variant-elem--low-stock' : '') + (notify ? ' variant-elem--coming-soon' : '');
      return (
        `<div class="variant-elem${modifier}" aria-available="${v.available}" data-product-id="${productId}" data-variant-id="${v.id}" data-product-image="${imageUrl}">` +
        `<span class="variant-elem__size">${v.title}</span>${v.lowStock ? clock : ''}${notify ? mail : ''}</div>`
      );
    }).join('');
  }

  /**
   * The "last units" clock icon, authored in snippets/quick-add-drawer.liquid so
   * the SVG has a single definition shared with the mobile quick-add drawer.
   */
  _clockIconHTML() {
    if (this._clockIcon === undefined) {
      this._clockIcon =
        document.getElementById('quick-add-clock-icon-template')?.innerHTML.trim() || '';
    }
    return this._clockIcon;
  }

  /**
   * The "Avísame" mail icon for out-of-stock size pills, authored in
   * snippets/quick-add-drawer.liquid alongside the clock.
   */
  _mailIconHTML() {
    if (this._mailIcon === undefined) {
      this._mailIcon =
        document.getElementById('quick-add-mail-icon-template')?.innerHTML.trim() || '';
    }
    return this._mailIcon;
  }

  /**
   * Convert normalized variants to JSON format for DOM storage
   */
  _variantsForJson(variants) {
    return variants.map(v => ({
      id: `gid://shopify/ProductVariant/${v.id}`,
      title: v.title,
      availableForSale: v.available,
      lowStock: !!v.lowStock,
      selectedOptions: [{ name: 'size', value: v.title }]
    }));
  }

  // ==========================================
  // Optimistic Rendering Helpers
  // ==========================================

  /**
   * Clone a product card from trendy section to wishlist container
   * Used for instant visual feedback when adding from "lo más trendy"
   */
  _addCardFromTrendyOptimistic(productId) {
    // Find the card in trendy section
    const trendyCard = document.querySelector(`.trendy-col .brn-product__fav[data-product="${productId}"]`);
    if (!trendyCard) return false;

    const gridItem = trendyCard.closest('.collection__grid__item');
    if (!gridItem) return false;

    const container = document.querySelector(this.sel.container);
    if (!container) return false;

    // Clone the card and add to wishlist
    const clone = gridItem.cloneNode(true);
    clone.classList.add('wishlist-optimistic');

    // Update heart button state in clone
    const cloneBtn = clone.querySelector('.brn-product__fav');
    if (cloneBtn) {
      cloneBtn.classList.add('active');
      cloneBtn.setAttribute('aria-pressed', 'true');
    }

    // Insert at the end of wishlist (consistent with API behavior)
    container.appendChild(clone);

    // Update trendy section visibility
    this._updateTrendyVisibility();

    return true;
  }

  /**
   * Update trendy section visibility based on wishlist count
   */
  _updateTrendyVisibility() {
    const trendyCol = document.querySelector(this.sel.trendy);
    if (!trendyCol) return;

    const totalProducts = this.getTotalProductCount();
    trendyCol.style.display = totalProducts <= 5 ? 'block' : 'none';
  }

  renderProductCards(products, containerSelector, options = {}) {
    const container = document.querySelector(containerSelector);
    if (!container) {
      console.error(`Container ${containerSelector} not found`);
      return;
    }

    if (!products || products.length === 0) {
      const emptyEl = document.querySelector(this.elems.WISHLIST_EMPTY);
      const loaderEl = document.querySelector(this.elems.WISHLIST_LOADER);
      if (emptyEl) emptyEl.style.display = 'block';
      if (loaderEl) loaderEl.style.display = 'none';
      return;
    }

    // Filter out null products
    const validProducts = products.filter(p => p !== null);

    const cardsHtml = validProducts.map((product, index) =>
      this.renderProductCard(product, {
        ...options,
        animationOrder: index + 1
      })
    ).join('');

    const wishlistLoginBanner = document.querySelector(this.elems.WISHLIST_LOGIN);
    if (wishlistLoginBanner) {
      wishlistLoginBanner.classList.add('active');
    }
    container.insertAdjacentHTML('beforeEnd', cardsHtml);

    const loaderEl = document.querySelector(this.elems.WISHLIST_LOADER);
    if (loaderEl) loaderEl.style.display = 'none';

    requestAnimationFrame(() => {
      this.updateAllButtons();
    });
  }

  /**
   * Render the wishlist page
   * Uses API for logged-in users (complete product data)
   * Uses Shopify GraphQL for anonymous users (product IDs only in localStorage)
   */
  async renderWishlist(containerSelector, listId = '0', options = {}) {
    const container = document.querySelector(containerSelector);
    if (!container) {
      console.error(`Container ${containerSelector} not found`);
      return;
    }

    // Clear existing products
    container.querySelectorAll('.collection__grid__item').forEach(item => item.remove());

    try {
      let products = [];

      if (this.isUserLoggedIn()) {
        // Fetch from API - returns complete product data
        const response = await this.apiClient.getWishlist(listId);
        products = response?.data?.products || [];

        // Fallback: API returned empty but metafield has products (migration / out-of-sync)
        // Render using Shopify Storefront GraphQL (same path as anonymous users)
        if (products.length === 0) {
          const metafieldIds = this._getProductIdsFromMetafield(listId);
          if (metafieldIds.length > 0) {
            console.log('[Wishlist] renderWishlist: API empty, falling back to metafield + Shopify GraphQL');
            const fetched = await this.fetchProductsFromShopify(metafieldIds);
            products = fetched?.filter(p => p !== null) || [];
          }
        }

        // Update cache with whatever we ended up with
        this.cachedProducts.clear();
        products.forEach(p => {
          const id = this._extractId(p);
          if (id) this.cachedProducts.set(id, p);
        });
      } else {
        // Anonymous user - fetch from Shopify using localStorage IDs
        const localProducts = this.data.lists[listId]?.products || [];
        if (localProducts.length > 0) {
          const fetched = (await this.fetchProductsFromShopify(localProducts)) || [];
          const valid = fetched.filter(p => p !== null);
          const resolvedIds = new Set(valid.map(p => this._extractId(p)));

          // Self-healing: snapshot every product the Storefront API still returns, so
          // items added before this cache existed (or before they sold out) have a
          // fallback once Shopify later drops them from the storefront context.
          valid.forEach(p => {
            const id = this._extractId(p);
            if (id) this.localMeta[id] = p;
          });
          this._saveLocalMeta();

          // The Storefront API excludes out-of-stock products from the storefront
          // context, so they come back null above. Fall back to the metadata snapshot
          // we cached at add-time so those products still render — and never purge
          // their IDs from localStorage, they remain valid wishlist entries.
          const fallback = localProducts
            .filter(id => !resolvedIds.has(id.toString()))
            .map(id => this.localMeta[id.toString()])
            .filter(Boolean);

          products = [...valid, ...fallback];
        }
      }

      const trendyCol = document.querySelector(this.elems.WISHLIST_TRENDY);

      if (trendyCol && products.length <= 5) {
        trendyCol.style.display = 'block';
      }

      this.renderProductCards(products, containerSelector, options);

    } catch (error) {
      console.error('Error rendering wishlist:', error);
      container.innerHTML = '<p class="wishlist__error">Error al cargar la wishlist</p>';
    }
  }

}

// Initialize wishlist singleton
const wishlist = new Wishlist({
  currentUrl: window.config.currentUrl,
  customerWishlist: window.customerWishlist,
  storageKey: window.config.wishlistStorageKey,
  apiBaseUrl: window.config.hydrogenEndpoint,
  storefrontAccessToken: window.config.storefrontAccessToken,
});

window.wishlist = wishlist;
